import { type DrawerProps, Box, Drawer, List, styled } from '@shared/components';
import { useEffect } from 'react';
import { matchPath, useLocation } from 'react-router-dom';

import { MENU_DRAWER_WIDTH_LARGE } from '../../constants';
import type { MenuItem } from '../../hooks';
import { useSidebar } from '../../providers';
import { RelativeMenuItem } from '../menu-item';

import SecondarySidebarHeader from './secondary-sidebar-header';
import SecondarySubMenu from './secondary-sub-menu';

type SecondaryMenuProps = {
  subMenuTitle?: string;
} & MenuItem;

interface SecondarySidebarProps {
  menuTitle?: string;
  secondaryMenu: SecondaryMenuProps[];
}

export const Sidebar = styled(Drawer, {
  name: 'Sidebar',
  slot: 'Root',
  overridesResolver: (props, styles) => [styles['root']],
})<DrawerProps>({
  '.MuiPaper-root': {
    position: 'relative',
    width: MENU_DRAWER_WIDTH_LARGE,
  },
});

function SecondarySidebar(props: Readonly<SecondarySidebarProps>) {
  const { menuTitle, secondaryMenu } = props;
  const { setIsSecondarySidebarMounted } = useSidebar();

  const { pathname: currentPath } = useLocation();

  const selectedMenu = secondaryMenu.find((item) => {
    const isOnParentMenuOrSameSubMenu = matchPath(
      { path: item.link as string, end: false },
      currentPath,
    );
    return isOnParentMenuOrSameSubMenu;
  });

  const subMenu = selectedMenu?.subMenu;

  // automatically minimize primary sidebar on mount
  useEffect(() => {
    setIsSecondarySidebarMounted(true);
    return () => setIsSecondarySidebarMounted(false);
  }, []);

  return (
    <Sidebar variant='permanent' open className='secondary'>
      <SecondarySidebarHeader menuTitle={menuTitle} />
      <List>
        {secondaryMenu?.map((item) => <RelativeMenuItem key={item.label as string} {...item} />)}
      </List>
      <Box pb={4} />
      {subMenu ? <SecondarySubMenu title={selectedMenu.subMenuTitle} subMenu={subMenu} /> : null}
      <Box id='mini-content-menu-container' sx={{ height: '100%', overflowY: 'hidden' }} />
    </Sidebar>
  );
}

export type { SecondaryMenuProps, SecondarySidebarProps };
export default SecondarySidebar;
